class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      // Already rendered (e.g., Theme Editor); ensure marquee behavior is initialized
      initMarquees(this);
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          initMarquees(this);
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

/**
 * Initialize marquee containers within a root element
 * @param {ParentNode|Document} root
 */
function initMarquees(root = document) {
  const containers = root.querySelectorAll('[data-marquee]:not([data-marquee-initialized])');
  containers.forEach((container) => {
    try {
      setupMarquee(container);
      container.setAttribute('data-marquee-initialized', 'true');
    } catch (e) {
      console.error('Marquee init error', e);
    }
  });
}

/**
 * Setup a single marquee container
 * @param {HTMLElement} container
 */
function setupMarquee(container) {
  const track = container.querySelector('.resource-list__marquee-track') || container;
  const speedAttr = parseFloat(container.getAttribute('data-speed'));
  const speed = Number.isFinite(speedAttr) ? speedAttr : 60; // px/s
  const pauseOnHover = container.getAttribute('data-pause-on-hover') === 'true';

  // Ensure base item list
  const baseItems = Array.from(track.children);
  if (!baseItems.length) return;

  // Measure and clone enough items to cover at least 2x the container width
  function getGap() {
    const styles = getComputedStyle(track);
    return parseFloat(styles.gap || styles.columnGap || '0') || 0;
  }

  function measureBaseWidth() {
    const g = getGap();
    const widths = baseItems.map((el) => el.getBoundingClientRect().width);
    const itemsGap = g * Math.max(0, baseItems.length - 1);
    return { baseWidth: widths.reduce((a, b) => a + b, 0) + itemsGap, gap: g };
  }

  let { baseWidth, gap } = measureBaseWidth();
  let baseUnit = baseWidth + gap; // include the end gap for seamless looping

  // Clone until width is sufficient
  function ensureClones() {
    // Remove previous clones
    Array.from(track.querySelectorAll('[data-marquee-clone]')).forEach((el) => el.remove());
    let totalWidth = track.scrollWidth;
    // If scrollWidth is 0 (not in DOM), fallback to baseWidth
    if (!totalWidth) totalWidth = baseWidth;
    // Ensure at least viewport + one full base unit ahead to avoid gaps at wrap
    const target = container.clientWidth + baseUnit * 1.5;
    while (totalWidth < target) {
      baseItems.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-marquee-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
      totalWidth = track.scrollWidth;
    }
  }

  ensureClones();

  let offset = 0; // negative values move left
  let lastTime = performance.now();
  let paused = false;
  let rafId = 0;

  function step(now) {
    const dt = Math.min(0.05, (now - lastTime) / 1000); // cap delta to avoid big jumps
    lastTime = now;
    if (!paused) {
      offset -= speed * dt;
      // Wrap offset by base unit (base width + one gap) to create seamless loop
      if (-offset >= baseUnit) {
        // preserve fractional remainder to avoid drift
        offset = -(((-offset) % baseUnit));
      }
      track.style.transform = `translate3d(${offset}px, 0, 0)`;
    }
    rafId = requestAnimationFrame(step);
  }

  function onResize() {
    // Recompute base width and clones
    const m = measureBaseWidth();
    baseWidth = m.baseWidth;
    gap = m.gap;
    baseUnit = baseWidth + gap;
    ensureClones();
  }

  const resizeObserver = new ResizeObserver(onResize);
  resizeObserver.observe(container);
  resizeObserver.observe(track);

  if (pauseOnHover) {
    container.addEventListener('pointerenter', () => (paused = true));
    container.addEventListener('pointerleave', () => (paused = false));
  }

  // Start animating
  rafId = requestAnimationFrame(step);

  // Cleanup when container is removed
  const abort = new AbortController();
  const observer = new MutationObserver(() => {
    if (!document.body.contains(container)) {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      observer.disconnect();
      abort.abort();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
